import React, { useState } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { icons } from './ui/Icons';

/**
 * Componente: CancelAppointmentModal
 * Modal para confirmar cancelamento de agendamento
 */
const CancelAppointmentModal = ({ isOpen, onClose, appointment, onConfirm, canCancel, hoursUntilAppointment }) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await onConfirm(reason);
    setIsSubmitting(false);
    setReason('');
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  if (!appointment) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Cancelar Agendamento">
      <div className="space-y-4">
        {/* Informações do agendamento */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-2">Detalhes do Agendamento</h4>
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center">
              {icons.building({ className: 'h-4 w-4 mr-2 text-indigo-600' })}
              <span><strong>Barbearia:</strong> {appointment.barbershopName}</span>
            </div>
            <div className="flex items-center">
              {icons.scissors({ className: 'h-4 w-4 mr-2 text-indigo-600' })}
              <span><strong>Serviço:</strong> {appointment.service}</span>
            </div>
            <div className="flex items-center">
              {icons.calendar({ className: 'h-4 w-4 mr-2 text-indigo-600' })}
              <span><strong>Data:</strong> {new Date(appointment.date + 'T' + appointment.time).toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
            </div>
            <div className="flex items-center">
              {icons.clock({ className: 'h-4 w-4 mr-2 text-indigo-600' })}
              <span><strong>Horário:</strong> {appointment.time}</span>
            </div>
            {appointment.servicePrice && (
              <div className="flex items-center">
                <span className="mr-2">💰</span>
                <span><strong>Valor:</strong> R$ {appointment.servicePrice.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Aviso de prazo */}
        {canCancel ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              {icons.alert({ className: 'h-5 w-5 text-yellow-600 mr-2 mt-0.5' })}
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">Atenção!</p>
                <p>Faltam aproximadamente <strong>{hoursUntilAppointment.toFixed(1)}h</strong> para o agendamento.</p>
                <p className="mt-1">Cancelamentos devem ser feitos com pelo menos 4 horas de antecedência para evitar transtornos.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              {icons.alert({ className: 'h-5 w-5 text-red-600 mr-2 mt-0.5' })}
              <div className="text-sm text-red-800">
                <p className="font-semibold mb-1">Não é possível cancelar</p>
                <p>Faltam apenas <strong>{hoursUntilAppointment.toFixed(1)}h</strong> para o agendamento.</p>
                <p className="mt-1">Cancelamentos só são permitidos com pelo menos <strong>4 horas de antecedência</strong>.</p>
                <p className="mt-2">Por favor, entre em contato diretamente com a barbearia para solicitar o cancelamento.</p>
              </div>
            </div>
          </div>
        )}

        {/* Campo de motivo (opcional) */}
        {canCancel && (
          <>
            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                Motivo do cancelamento (opcional)
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Ex: Imprevisto, mudança de horário, etc."
              />
            </div>

            {/* Botões de ação */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1"
              >
                Voltar
              </Button>
              <Button
                variant="danger"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? (
                  <>
                    <span className="inline-block animate-spin mr-2">⏳</span>
                    Cancelando...
                  </>
                ) : (
                  <>
                    {icons.x({ className: 'h-4 w-4 mr-2 inline' })}
                    Confirmar Cancelamento
                  </>
                )}
              </Button>
            </div>
          </>
        )}

        {/* Botão de fechar quando não pode cancelar */}
        {!canCancel && (
          <div className="pt-2">
            <Button
              variant="secondary"
              onClick={handleClose}
              className="w-full"
            >
              Entendi
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CancelAppointmentModal;
